import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { CementingLanding } from 'src/app/wellbore-inputs/models/cementing-landing.model';
import { ExternalPressureProfileType, GeneralEPP, TrappedCasingPressureEPP } from '../../models/external-pressure-profile.model';
import { getControlErrors, isControlInvalid } from 'src/app/shared/services/validation-helpers';
import { UntypedFormControl, Validators } from '@angular/forms';
import { TrajectoryService } from 'src/app/shared/services/trajectory.service';
import { unitsLib } from 'src/app/core/services/unit-library';
import { LinearInterpSingle } from 'src/app/perivis/shared/helpers/linear-interp-single.helper';
import { GetValueFromPpg } from 'src/app/perivis/shared/helpers/units.helper';
import { lastValueFrom } from 'rxjs';
import { PoreFrac } from 'src/app/perivis/models/poreFrac.model';
import { WellComponent } from 'src/app/wellbore-inputs/components/well-configuration/models/well-component';
import { FluidsService } from 'src/app/perical/services/fluids.service';
import { UomQuantityInput } from '../../uom-quantity-input/uom-quantity-input-component';

interface TrappedCasingPressureForm {
  type: ExternalPressureProfileType.TRAPPEDCASINGPRESSURE;
  generalEpp: GeneralEPP;
  allowFractureAtShoe: boolean;
  fractureDepth: UomQuantityInput;
  fractureGradient: UomQuantityInput;
  fractureMarginOfError: UomQuantityInput;
  hydraulicInterfaceDepth: UomQuantityInput;
}

@Component({
  selector: 'app-trapped-casing-pressure',
  templateUrl: './trapped-casing-pressure.component.html',
  providers: subformComponentProviders(TrappedCasingPressureComponent),
  standalone: false
})
export class TrappedCasingPressureComponent implements OnChanges, OnInit {
  @Input() currentString: any;
  @Input() cement: CementingLanding;
  @Input() hangerMd: any;
  @Input() userUnits: UserUnitsModel;
  @Input() loadCaseIndex: number;
  @Input() fracPressures: PoreFrac[]
  @Input() tubulars: WellComponent[];

  public fractureGradientValidation: { min: number, max: number };
  public marginOfErrorValidation: { min: number, max: number };
  public fracDepthsRange: { min: number, max: number } = { min: 0, max: 0 };
  public hydraulicInterfaceDepthRange: { min: number, max: number } ;  
  public densityLabel: string;
  public nextStringMudWeight: number;
  public lengthUnitLabel: string;
  public fractureDepthDefault: number;
  public defaultFracGradient: number;
  public hydraulicInterfaceDepthDefault: number;
  public validators: any;
  public fracAtShoe: boolean;

  // Validation delegates
  public isControlInvalid: Function = isControlInvalid;
  public getControlErrors: Function = getControlErrors;

  
  constructor(private _trajectoryService: TrajectoryService, private _fluidsService: FluidsService) { }

  public form = createForm<TrappedCasingPressureEPP, TrappedCasingPressureForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(ExternalPressureProfileType.TRAPPEDCASINGPRESSURE),
      generalEpp: new UntypedFormControl(null, Validators.required),
      allowFractureAtShoe: new UntypedFormControl(false),
      fractureDepth: new UntypedFormControl(null, [Validators.required, Validators.min(0)]),
      fractureGradient: new UntypedFormControl(null, [Validators.required, Validators.min(0)]),
      fractureMarginOfError: new UntypedFormControl(null, [Validators.required, Validators.min(0)]),
      hydraulicInterfaceDepth: new UntypedFormControl(null, [Validators.required, Validators.min(0)])
    },
    toFormGroup: (obj: TrappedCasingPressureEPP): TrappedCasingPressureForm => {
      this.allowFracAtShoeChange(obj.allowFractureAtShoe);

      return {
        type: ExternalPressureProfileType.TRAPPEDCASINGPRESSURE,
        generalEpp: { fluidDensity: obj.fluidDensity, hangerPressure: obj.hangerPressure, hasDeterioratedMud: obj.hasDeterioratedMud, deterioratedMudDensity: obj.deterioratedMudDensity, isLeakAtHanger: obj.isLeakAtHanger },
        allowFractureAtShoe: obj.allowFractureAtShoe,
        fractureDepth:  { control: obj.fractureDepth },
        fractureGradient: { control: obj.fractureGradient },
        fractureMarginOfError: { control: obj.fractureMarginOfError },
        hydraulicInterfaceDepth: { control: obj.hydraulicInterfaceDepth }
      };
    },
    fromFormGroup: (formValue: TrappedCasingPressureForm): TrappedCasingPressureEPP => {
      const { generalEpp, allowFractureAtShoe, fractureDepth, fractureGradient, fractureMarginOfError, hydraulicInterfaceDepth } = formValue;
      return {
        type: ExternalPressureProfileType.TRAPPEDCASINGPRESSURE,
        hangerPressure: generalEpp?.hangerPressure,
        fluidDensity: generalEpp?.fluidDensity,
        hasDeterioratedMud: generalEpp.hasDeterioratedMud,
        deterioratedMudDensity: generalEpp?.deterioratedMudDensity,
        isLeakAtHanger: generalEpp?.isLeakAtHanger,
        allowFractureAtShoe: allowFractureAtShoe,
        fractureDepth: fractureDepth?.control,
        fractureGradient: fractureGradient?.control,
        fractureMarginOfError: fractureMarginOfError?.control,
        hydraulicInterfaceDepth: hydraulicInterfaceDepth?.control
      };
    }
  });

  ngOnInit(): void {
    this.setDefaults();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.densityLabel = unitsLib[this.userUnits.density].symbol;
    this.lengthUnitLabel = this.userUnits.longLengths;

    switch (this.densityLabel) {
      case 'ppg':
        this.fractureGradientValidation = { min: 1, max: 25 };
        this.marginOfErrorValidation = { min: 0, max: 10 };
        break;
      case 'kg/m³':
      case 'g/L':
        this.fractureGradientValidation = { min: 119.8, max: 2995 };
        this.marginOfErrorValidation = { min: 0, max: 1198 };
        break;
      case 'g/cm³':
      case 'kg/l':
      case 'sg':
        this.fractureGradientValidation = { min: 0.119, max: 3 };
        this.marginOfErrorValidation = { min: 0, max: 1.2 }
        break;
      case 'g/m³':
        this.fractureGradientValidation = { min: 119826, max: 2995660 };
        this.marginOfErrorValidation = { min: 0, max: 1198264 }
        break;
    }

    this.validators = {
      marginOfError: [Validators.min(this.marginOfErrorValidation.min), Validators.max(this.marginOfErrorValidation.max), Validators.required],
      fractureGradient: [Validators.min(this.fractureGradientValidation.min), Validators.max(this.fractureGradientValidation.max), Validators.required],
      hydraulicInterfaceDepth: [Validators.min(this.hydraulicInterfaceDepthRange?.min), Validators.max(this.hydraulicInterfaceDepthRange?.max), Validators.required]
    }

    if (changes.currentString || changes.fracPressures) {
      this.updateRangeAndFluidDensity();
      // this.getEmwDefaultFrac(this.fractureDepthDefault, false);
    }
  }

  public fractureGradientChange() {
    if (!this.form.formGroup.controls.fractureGradient.value) {
      this.getEmwDefaultFrac(this.form.formGroup.controls.fractureDepth.value, true);
    }
  }

  async getEmwDefaultFrac(fracDepth, updateFracGradient: boolean): Promise<void> {
    const tvds = await lastValueFrom(this._trajectoryService.getTvdsFromMds([fracDepth]));

    if (this.fracPressures?.length > 1) {
      this.defaultFracGradient = +LinearInterpSingle(tvds[0], this.fracPressures, this.userUnits).toFixed(2);
    } else {
      this.defaultFracGradient = this.currentString.annularFluid.state.nominalDensity + GetValueFromPpg(2, this.densityLabel);
    }
    if ((!this.form.formGroup.controls.fractureGradient.value || updateFracGradient)) {
      this.form.formGroup.controls.fractureGradient.setValue({ control: this.defaultFracGradient });
    }
  }

  public fractureDepthChange(updateFracGradient?: boolean): void {
    if (!this.form.formGroup.controls.fractureDepth.invalid) {
      this.getEmwDefaultFrac(this.form.formGroup.controls.fractureDepth.value.control, updateFracGradient);
    }
  }

  private setDefaults() {
    const selectedStringIndex = this.tubulars.findIndex(x => x.id === this.currentString.id);
    const nextString = this.tubulars[selectedStringIndex + 1];
    let previousString = null;

    // this.currentStringMudWeight = this.currentString.annularFluid?.state['nominalDensity'] ?? 0;
    const nextShoeDepth = Math.max(nextString?.shoeMd ?? 0, this.currentString.shoeMd);
    switch (this.currentString.type) {
      case 'Casing': {
        previousString = this.tubulars[selectedStringIndex - 1];
        this.checkNextLinerShoeDepth(selectedStringIndex, nextShoeDepth);
        break;
      }
      case 'Liner': {
        previousString = this.tubulars[selectedStringIndex - 1];
        this.checkNextLinerShoeDepth(selectedStringIndex, nextShoeDepth);
        break;
      }
      case 'Tieback': {
        previousString = this.tubulars[selectedStringIndex - 1];
        const previousStringIndex = this.tubulars.findIndex(t => t.shoeMd === previousString.shoeMd) - 1;
        this.checkNextLinerShoeDepth(previousStringIndex, previousString.shoeMd);
        break;
      }
      case 'Tubing': {
        // TODO Not needed?
        this.nextStringMudWeight = this.currentString.annularFluid?.state['nominalDensity'] ?? 0;
        break;
      }
    };

    const topOfCement = this.currentString.topOfCementMd ?? 0;
    const hangerMd = this.currentString.hangerMd ?? 0;
    const currentShoeMd = this.currentString.shoeMd ?? 0;
    
    if (topOfCement == hangerMd) {
      this.hydraulicInterfaceDepthRange = { min: hangerMd, max: currentShoeMd };
      this.hydraulicInterfaceDepthDefault = hangerMd;
    } else if (topOfCement < previousString.shoeMd) {
      const midpoint = (topOfCement + currentShoeMd) / 2;
      const defaultHydraulicInterfaceDepth = Math.max(midpoint, previousString.shoeMd);
      this.hydraulicInterfaceDepthRange = { min: previousString.shoeMd, max: currentShoeMd };
      this.hydraulicInterfaceDepthDefault = defaultHydraulicInterfaceDepth;
    } else if (topOfCement > previousString.shoeMd) {
      const defaultHydraulicInterfaceDepth = (topOfCement + currentShoeMd) / 2;
      this.hydraulicInterfaceDepthRange = { min: topOfCement, max: currentShoeMd };
      this.hydraulicInterfaceDepthDefault = defaultHydraulicInterfaceDepth;
    }
  }

  private checkNextLinerShoeDepth(selectedStringIndex: number, nextOrLinerShoeDepth: number): void {
    let deepestNextLiner = null;
    let tubular = null;
    for (let i = selectedStringIndex + 1; i < this.tubulars.length; i++) {
      tubular = this.tubulars[i];
      if (tubular.type === 'Liner') {
          deepestNextLiner = tubular.shoeMd;
          break;
      } else if (tubular.type === 'Tieback') {
        const linerItsTiedBackTo = this.tubulars.find(t =>
          t.type === 'Liner' &&
          t.hangerMd === tubular.shoeMd
        ).shoeMd;
        deepestNextLiner = linerItsTiedBackTo;
        break;
      } else {
        deepestNextLiner = tubular.shoeMd;
        break;
      }
    }

    this.fracDepthsRange = { 
      min: this.currentString.type == 'Tieback' ? nextOrLinerShoeDepth : this.currentString.shoeMd, 
      max: deepestNextLiner ?? this.currentString.shoeMd
    };

    this.updateRangeAndFluidDensity();
  }

  private updateRangeAndFluidDensity(): void {
    const currentStringIndex = this.tubulars.findIndex(t => t.id === this.currentString.id);
    
    this.fractureDepthDefault = this.fracDepthsRange.min;
    let maxValidation = this.fractureDepthDefault;
    let annularFluidId = null;
    for (let i = currentStringIndex; i < this.tubulars.length; i++) {
      const tubular = this.tubulars[i];
      if ((tubular.shoeMd > this.fracDepthsRange.max && i == tubular.sequenceNumber) ||
       (tubular.shoeMd >= this.fracDepthsRange.max && i < tubular.sequenceNumber) ||
       (tubular.shoeMd >= this.fracDepthsRange.max && i == tubular.sequenceNumber && tubular.type != 'Tubing')
      ) { 
        annularFluidId = tubular.annularFluidId;

        if (tubular.type == 'Tieback') {
          maxValidation = this.tubulars[i - 1].shoeMd;
          break;
        } else if (tubular.type == 'Liner') {
          maxValidation = tubular.shoeMd;

        for (let j = i - 1; j >= 0; j--) {
          if (this.tubulars[j].type == 'Tieback') {
            continue;
          } else {
            this.fractureDepthDefault = this.tubulars[j].shoeMd;
            break;
          }
        }
          continue;
        } else if (tubular.type == 'Casing') {
          maxValidation = tubular.shoeMd;
        } else if (tubular.type == 'Tubing') {
          this.fracDepthsRange.max = maxValidation;
          break;
        }

        if (this.tubulars[i].type == 'Tieback') {
          const linerItsTiedBackTo = this.tubulars.find(t =>
            t.type === 'Liner' &&
            t.hangerMd === tubular.shoeMd
          ).shoeMd;
          this.fractureDepthDefault = linerItsTiedBackTo;
        } else {
          this.fractureDepthDefault = this.tubulars[i - 1].shoeMd;
        }
        break;
      } else if (tubular.type == 'Tieback') {
        const selectedStringIndex = this.tubulars.findIndex(t => t.id === this.currentString.id);
        const tiedBackLinerIndex = this.tubulars.findIndex(t =>
          t.type === 'Liner' &&
          t.hangerMd === tubular.shoeMd
        );

        if (selectedStringIndex < tiedBackLinerIndex) {
          maxValidation = this.tubulars[i - 1].shoeMd;
          break;
        }

        maxValidation = this.tubulars[i - 1].shoeMd;
        continue;
      }
    }

    this.fracDepthsRange.max = maxValidation;

    if (annularFluidId && annularFluidId !== this.currentString.annularFluidId) {
      this._fluidsService.getFluidById(annularFluidId).subscribe(fluid => {
        this.nextStringMudWeight = fluid.state['nominalDensity'];
      });
    } else {
      this.nextStringMudWeight = this.currentString.annularFluid?.state['nominalDensity'] ?? 0;
    }

    this.fracDepthsRange = { 
      min: this.fracDepthsRange.min,
      max: this.fracDepthsRange.max
    };

    this.fractureDepthDefault = Math.max(this.fracDepthsRange.min, this.fractureDepthDefault);

    const fractureDepthValid = this.form.formGroup.controls.fractureDepth.invalid;

    setTimeout(() => {
      this.fractureDepthChange(!fractureDepthValid);
    }, 50);
  }

  public allowFracAtShoeChange(fracAtShoe?: boolean): void {
    const allowFracAtShoe = fracAtShoe ?? this.form.formGroup.controls.allowFractureAtShoe.value;
    this.fracAtShoe = allowFracAtShoe;
  }
}
